import Script from 'next/script';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { recaptchaService } from '@app/services/recaptcha.service';

import { useToast } from './ui';

declare global {
  interface Window {
    grecaptcha: any;
    onGRecaptchaLoad?: () => void;
  }
}

type GCaptchaProps = {
  siteKey?: string;
  theme?: 'light' | 'dark';
  size?: 'normal' | 'compact';
  tabindex?: number;
  onVerify: (token: string) => void;
  onExpired?: () => void;
  onError?: () => void;
  onNoCaptcha?: () => void;
  className?: string;
};

const getClientEnv = () => {
  return {
    siteKey: process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITEKEY || '',
  };
};

export default function GCaptcha({
  siteKey = getClientEnv().siteKey,
  theme = 'light',
  size = 'normal',
  tabindex = 0,
  onVerify,
  onExpired,
  onError,
  onNoCaptcha,
  className,
}: GCaptchaProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);
  const [scriptReady, setScriptReady] = useState<boolean>(false);
  const toast = useToast();

  const handleVerify = useCallback(async (token: string) => {
    try {
      const data = await recaptchaService.googleVerify(token);
      if (data?.success) {
        onVerify(token);
      } else {
        onError && onError();
        toast.error('Xác thực reCAPTCHA thất bại');
      }
    } catch (e) {
      onError && onError();
      toast.error('Xác thực reCAPTCHA thất bại');
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.onGRecaptchaLoad = () => setScriptReady(true);

    if (window.grecaptcha && window.grecaptcha.render) setScriptReady(true);
  }, []);

  useEffect(() => {
    if (!scriptReady || !containerRef.current) return;
    if (!window.grecaptcha || !window.grecaptcha.render) return;
    if (widgetIdRef.current != null) return;

    try {
      const id = window.grecaptcha.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        size,
        tabindex,
        callback: (token: string) => handleVerify(token),
        'expired-callback': () => onExpired && onExpired(),
        'error-callback': () => onError && onError(),
      });
      widgetIdRef.current = id;
    } catch (e) {
      onError && onError();
    }
  }, [scriptReady, siteKey, theme, size, tabindex, onVerify, onExpired, onError]);

  if (!siteKey) {
    onNoCaptcha?.();

    return <></>;
  }

  return (
    <>
      <Script
        src="https://www.google.com/recaptcha/api.js?onload=onGRecaptchaLoad&render=explicit"
        strategy="afterInteractive"
        onLoad={() => {
          if (typeof window !== 'undefined') {
            if (window.grecaptcha && window.grecaptcha.render) setScriptReady(true);
          }
        }}
      />
      <div className={className}>
        <div ref={containerRef} />
        {!siteKey && <span style={{ display: 'none' }}>Missing reCAPTCHA site key</span>}
      </div>
    </>
  );
}
